import React, { useState, useCallback } from 'react';
import { Form, Input, Tooltip, FormInstance } from 'antd';
import { t } from 'i18next';
import epaxios from '@/utils/epaxios';
import { EPButton, EPSelect, EPSelectOption } from '@/components';
import QuestionCircleOutlined from '@/comp/common/icons/QuestionCircleOutlined';
import { HomeContentContainer } from './HomeContent.styles';
import { token } from '@/theme';
import { observer } from 'mobx-react';
import { useStores } from '@/hooks/use-stores';
import { Rule } from 'antd/lib/form';
import { OnboardingFormPayload } from '@/store/SiteSurveyForm/constants';
import {
  getFormDisplayName,
  getAvailableRegions,
  createHandleRegionChange,
} from '@/comp/Forms/SiteSurveyForm/AccountSelectionStep/Homecontent.utils';

interface HomeContentProps {
  isSubmitting: boolean;
  isLoading: boolean;
  getButtonText: () => string;
  form?: FormInstance;
}

const HomeContent: React.FC<HomeContentProps> = observer(({ isSubmitting, isLoading, getButtonText, form }) => {
  const { siteSurveyStore, userStore } = useStores();
  const [selectedFormId, setSelectedForm] = useState(null);

  const createAccountIdValidator = () => async (_: unknown, value: string) => {
    if (!value) {
      return Promise.resolve();
    }
    try {
      await siteSurveyStore.fetchAccountData(value);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(new Error(t('Salesforce account ID not found')));
    }
  };

  const onClickContinueEditing = async (): Promise<void> => {
    if (!selectedFormId) {
      return;
    }

    const formData = siteSurveyStore?.existingFormsBySalesforceAccount.find(form => form.formId === selectedFormId);
    if (!formData) {
      return;
    }

    if (form) {
      form.setFieldValue('selectedFormId', selectedFormId);
    }

    const response = await epaxios.get<OnboardingFormPayload>(`info-collection/onboarding-forms/${selectedFormId}`);

    await siteSurveyStore.updateStateByExistingForm(response.data);
    siteSurveyStore.setCurrentStep(1);
  };

  const accountIdRules: Rule[] = [
    {
      required: true,
      message: t('accountRequired'),
      validateTrigger: 'onBlur',
    },
    {
      validator: createAccountIdValidator(),
      validateTrigger: ['onBlur', 'onSubmit'],
    },
  ];

  const handleRegionChange = useCallback(createHandleRegionChange(siteSurveyStore, form), [siteSurveyStore, form]);

  return (
    <HomeContentContainer>
      <div className="welcome-header">
        <div className="welcome-title">
          {siteSurveyStore?.existingFormsBySalesforceAccount?.length > 0
            ? t('siteFormAlreadyExists')
            : t('Welcome to the site survey')}
        </div>
        <div className="welcome-description">
          {siteSurveyStore?.existingFormsBySalesforceAccount?.length > 0
            ? t('canContinueEditing')
            : t('welcomeDescription')}
        </div>
      </div>
      <div className="welcome-form-container">
        <Form.Item name="accountId" rules={accountIdRules} validateFirst={true} className="account-id-item">
          <Input
            placeholder={t('Add Salesforce account ID')}
            autoComplete="off"
            size="large"
            suffix={
              <Tooltip title={t('accountTooltip')} placement="right" color={token.colorBgContainer}>
                <span>
                  <QuestionCircleOutlined />
                </span>
              </Tooltip>
            }
          />
        </Form.Item>

        <Form.Item name="region" className="region-item" rules={[{ required: true, message: t('regionRequired') }]}>
          <EPSelect
            placeholder={t('Select region')}
            size="large"
            style={{ width: '100%' }}
            onChange={handleRegionChange}
            value={siteSurveyStore.selectedRegion}
            optionFilterProp="children"
            showSearch
          >
            {getAvailableRegions(userStore.user?.regions || []).map(region => (
              <EPSelectOption key={region.key} value={region.key}>
                {region.label}
              </EPSelectOption>
            ))}
          </EPSelect>
        </Form.Item>
        {siteSurveyStore?.existingFormsBySalesforceAccount?.length > 0 && (
          <div style={{ width: '100%' }}>
            <p> {t('availableSitesForm')}</p>

            <Form.Item name="selectedFormId">
              <EPSelect
                style={{ width: '100%' }}
                onChange={value => {
                  setSelectedForm(value);
                  if (form) {
                    form.setFieldValue('selectedFormId', value);
                  }
                }}
                value={selectedFormId}
                optionFilterProp="children"
                showSearch
                dropdownRender={menu => <>{menu}</>}
              >
                {siteSurveyStore?.existingFormsBySalesforceAccount?.map((form: OnboardingFormPayload) => {
                  return (
                    <EPSelectOption key={form.formId} value={form.formId}>
                      {getFormDisplayName(form)}
                    </EPSelectOption>
                  );
                })}
              </EPSelect>
            </Form.Item>
          </div>
        )}

        <div className="button-wrapper">
          {siteSurveyStore?.existingFormsBySalesforceAccount?.length > 0 ? (
            <EPButton type="primary" loading={isSubmitting || isLoading} onClick={onClickContinueEditing}>
              {t('continueEditingForm')}
            </EPButton>
          ) : (
            <EPButton
              type="primary"
              htmlType="submit"
              loading={isSubmitting || isLoading || siteSurveyStore?.isLoading}
            >
              {getButtonText()}
            </EPButton>
          )}
        </div>
        {siteSurveyStore?.existingFormsBySalesforceAccount?.length > 0 && (
          <EPButton
            type="link"
            onClick={() => {
              if (form) {
                const accountID = form.getFieldValue('salesforceAccountId');
                form.resetFields();
                form.setFieldValue('salesforceAccountId', accountID);
              }
              siteSurveyStore.setCurrentStep(1);
            }}
            style={{ color: token.colorPrimary }}
          >
            {t('startNewSurvey', 'Start new survey')}
          </EPButton>
        )}
      </div>
    </HomeContentContainer>
  );
});

export default HomeContent;
